import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Button, Chip, CircularProgress, MenuItem, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { AuthService } from '../../services/auth.service';
import { MaintenanceService } from '../../services/maintenance.service';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { PERMISSIONS } from '../../constants/permissions';
import { PmPlanDTO } from '../../interfaces/maintenance';
import { cell } from './woCommon';
import { PM_STATE_COLOR, PM_STATE_LABEL, pmDueHint, pmNextDueText, pmTriggerText } from './pmCommon';

const HORIZONS = [3, 7, 14, 30];

/** Overdue and due-soon PM plans. Managers can run the generator now instead of waiting for the nightly job. */
const PmDueList: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { hasPermission } = useAuth();
    const canManage = hasPermission(PERMISSIONS.MAINTENANCE_PM_MANAGE);
    const institutionId = AuthService.getSelectedInstitution()!;
    const [rows, setRows] = useState<PmPlanDTO[]>([]);
    const [loading, setLoading] = useState(true);
    const [days, setDays] = useState(7);
    const [generating, setGenerating] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setRows(await MaintenanceService.listPmDue(institutionId, days));
        } catch (e) {
            toast.showApiError(e, 'Failed to load due PM plans');
        } finally {
            setLoading(false);
        }
    }, [institutionId, days]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const generate = async () => {
        try {
            setGenerating(true);
            const r = await MaintenanceService.generatePmNow(institutionId);
            toast.showSuccess(`${r.created} work order(s) created, ${r.skipped} already open${r.failed ? `, ${r.failed} failed` : ''}`);
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to generate PM work orders');
        } finally {
            setGenerating(false);
        }
    };

    const overdue = rows.filter(r => r.state === 'OVERDUE').length;

    return (
        <Box sx={{ p: 2 }} data-help-page="maint.pmDue.page">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h5">PM due</Typography>
                {canManage && (
                    <Button data-help="maint.pmDue.generate" variant="contained" disabled={generating} onClick={generate}>Generate work orders now</Button>
                )}
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }} data-help-section="maint.pmDue.filter">
                <TextField
                    select size="small" label="Look ahead" value={days} sx={cell(150)} data-help="maint.pmDue.filter.days"
                    onChange={e => setDays(Number(e.target.value))}
                >
                    {HORIZONS.map(d => <MenuItem key={d} value={d}>{d} days</MenuItem>)}
                </TextField>
                <Typography variant="body2" sx={{ flex: '1 1 200px' }}>
                    {rows.length} plan(s), {overdue} overdue
                </Typography>
            </Box>

            <Paper data-help-section="maint.pmDue.table">
                {loading ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="maint.pmDue.col.machine">Machine</TableCell>
                                    <TableCell data-help="maint.pmDue.col.plan">Plan</TableCell>
                                    <TableCell data-help="maint.pmDue.col.repeats">Repeats</TableCell>
                                    <TableCell data-help="maint.pmDue.col.next">Next due</TableCell>
                                    <TableCell data-help="maint.pmDue.col.order">Open order</TableCell>
                                    <TableCell data-help="maint.pmDue.col.state">State</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.length === 0 && (
                                    <TableRow><TableCell colSpan={6} align="center">Nothing is due</TableCell></TableRow>
                                )}
                                {rows.map(r => (
                                    <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/maintenance/pm-plans/${r.id}`)}>
                                        <TableCell>{r.equipmentCode} {r.equipmentName}</TableCell>
                                        <TableCell>{r.name}</TableCell>
                                        <TableCell>{pmTriggerText(r)}</TableCell>
                                        <TableCell>{pmNextDueText(r)}</TableCell>
                                        <TableCell>
                                            {r.openWorkOrderId ? (
                                                <Button
                                                    data-help="maint.pmDue.action.order"
                                                    size="small"
                                                    onClick={e => { e.stopPropagation(); navigate(`/maintenance/work-orders/${r.openWorkOrderId}`); }}
                                                >
                                                    {r.openWorkOrderNo}
                                                </Button>
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell>
                                            <Chip size="small" label={PM_STATE_LABEL[r.state]} color={PM_STATE_COLOR[r.state]} />
                                            {pmDueHint(r) && <Typography variant="caption" sx={{ ml: 1 }}>{pmDueHint(r)}</Typography>}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </Box>
    );
};

export default PmDueList;
